import { ComponentProps } from "react";
import { ComputeFieldComponent } from "../computeFields";
import { Multipleprint } from "../types";
import { DimensionsFormatting } from "./shared/dimensions";
import { SupportTechniqueCF } from "./shared/technique";
import { SignatureCF } from "./shared/signature";
import { TitleCF } from "./shared/title";
import { RelatedAuthorsListCF } from "./shared/related-authors";
import { SeparatedList } from "./shared/utils";
import { InscriptionDosCF } from "./shared/inscription-dos";

export const MultipleprintDimensionsCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "estampesMultiplesDescriptionDimensionsOeuvre"
    | "estampesMultiplesDescriptionDimensionsOeuvreInconnues"
  >
> = ({ input, ...props }) => (
  <DimensionsFormatting
    input={{
      ...input.estampesMultiplesDescriptionDimensionsOeuvre,
      unknownDimensions:
        input.estampesMultiplesDescriptionDimensionsOeuvreInconnues,
    }}
    {...props}
  />
);

export const MultipleprintDimensionImageCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "estampesMultiplesDescriptionDimensionsImage"
    | "estampesMultiplesDescriptionDimensionsImageInconnues"
  >
> = ({ input, ...props }) => (
  <DimensionsFormatting
    input={{
      ...input.estampesMultiplesDescriptionDimensionsImage,
      unknownDimensions:
        input.estampesMultiplesDescriptionDimensionsImageInconnues,
    }}
    {...props}
  />
);

export const MultipleprintRelatedAuthorsCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "relatedAuthor"
    | "estampesMultiplesDescriptionAuteurArtisteAnonyme"
    | "estampesMultiplesDescriptionAuteurArtisteDApres"
    | "estampesMultiplesDescriptionAuteurArtisteAttribueA"
    | "estampesMultiplesDescriptionAuteurArtisteDansLeGoutDe"
    | "estampesMultiplesDescriptionAuteurArtisteEntourageDe"
    | "estampesMultiplesDescriptionAuteurAtelierDe"
  >
> = ({ input, ...props }) => (
  <RelatedAuthorsListCF
    input={{
      relatedAuthors: input.relatedAuthor,
      attributedTo: input.estampesMultiplesDescriptionAuteurArtisteAttribueA,
      from: input.estampesMultiplesDescriptionAuteurArtisteDApres,
      tasteOf: input.estampesMultiplesDescriptionAuteurArtisteDansLeGoutDe,
      entourageOf: input.estampesMultiplesDescriptionAuteurArtisteEntourageDe,
      studioOf: input.estampesMultiplesDescriptionAuteurAtelierDe,
      anonymous: input.estampesMultiplesDescriptionAuteurArtisteAnonyme,
    }}
    {...props}
  />
);

export const MultipleprintSupportTechniqueCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "estampesMultiplesDescriptionTechnique"
    | "estampesMultiplesDescriptionSupportMateriau"
    | "estampesMultiplesDescriptionOeuvreDoubleFace"
  >
> = ({ input, ...props }) => (
  <SupportTechniqueCF
    input={{
      technique: input.estampesMultiplesDescriptionTechnique,
      support: input.estampesMultiplesDescriptionSupportMateriau,
      isDoubleSided: input.estampesMultiplesDescriptionOeuvreDoubleFace,
    }}
    {...props}
  />
);

export const MultipleprintSignatureCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "estampesMultiplesDescriptionSignatureLocalisation"
    | "estampesMultiplesDescriptionSignatureTranscription"
  >
> = ({ input, ...props }) => (
  <SignatureCF
    input={{
      location: input.estampesMultiplesDescriptionSignatureLocalisation,
      transcription: input.estampesMultiplesDescriptionSignatureTranscription,
    }}
    {...props}
  />
);

export const MultipleprintInscriptionDosCF: ComputeFieldComponent<
  Pick<Multipleprint, "estampesMultiplesDescriptionInscriptionsDos">
> = ({ input, ...props }) => (
  <InscriptionDosCF
    input={{
      inscription: input.estampesMultiplesDescriptionInscriptionsDos,
    }}
    {...props}
  />
);

export const MultipleprintTitleCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "estampesMultiplesDescriptionTitreSansTitre"
    | "estampesMultiplesDescriptionTitreInconnu"
    | "estampesMultiplesDescriptionTitreIncertin"
    | "estampesMultiplesDescriptionTitre"
    | "estampesMultiplesDescriptionSousTitre"
    | "estampesMultiplesDescriptionDateDeCreation"
    | "estampesMultiplesDescriptionDateDeCreationInconnue"
  >
> = ({ input, ...props }) => (
  <TitleCF
    input={{
      title: input.estampesMultiplesDescriptionTitre,
      titleState: {
        none: input.estampesMultiplesDescriptionTitreSansTitre,
        unknown: input.estampesMultiplesDescriptionTitreInconnu,
        notConfirmed: input.estampesMultiplesDescriptionTitreIncertin,
      },
      subtitle: input.estampesMultiplesDescriptionSousTitre,
      date: input.estampesMultiplesDescriptionDateDeCreation,
      unknownDate: input.estampesMultiplesDescriptionDateDeCreationInconnue,
    }}
    {...props}
  />
);

export const MultipleprintSummaryCF: ComputeFieldComponent<
  ComponentProps<typeof MultipleprintRelatedAuthorsCF>["input"] &
    ComponentProps<typeof MultipleprintTitleCF>["input"] &
    ComponentProps<typeof MultipleprintDimensionsCF>["input"] &
    ComponentProps<typeof MultipleprintSupportTechniqueCF>["input"] &
    ComponentProps<typeof MultipleprintSignatureCF>["input"] &
    ComponentProps<typeof MultipleprintInscriptionDosCF>["input"]
> = ({ input, lineBreak = true, ...props }) => {
  return (
    <SeparatedList
      lineBreak={lineBreak}
      {...props}
      input={[
        <MultipleprintRelatedAuthorsCF input={input} {...props} />,
        <MultipleprintTitleCF input={input} {...props} />,
        <MultipleprintDimensionsCF input={input} {...props} />,
        <MultipleprintSupportTechniqueCF input={input} {...props} />,
        <MultipleprintSignatureCF input={input} {...props} />,
        <MultipleprintInscriptionDosCF input={input} {...props} />,
      ]}
    />
  );
};
